// NPC bodies painted at their on-screen size, 30x48 hd px, at the same density as the runners (runner-detail.ts), with
// one outfit per faction: police (cap and eyepiece), riot police (helmet and visor), checkpoint guards (hi-vis vest),
// elites (dark plate and a red visor slit), Oni-Gumi (sukajan, chain, shades), Kumadori-kai (black suits, kumadori
// paint), Benikage (haori with a beni lining), Kaminari-gumi on foot (pink leathers, helmet, lightning back) and
// civilians (four outfits x hair x bags, chosen from the NPC id). NPCs never wear a crew mask, a chest rig or crew LEDs,
// so they can't be mistaken for players. Colors are ramps of theme.ts only; the 1 px ink outline comes from outline().
import { canvas } from './canvas';
import type { Look } from './art';
import { K, px, dk, lt, mix } from './hd-kit';
import { STATE } from './theme';
import { poly, outline, RD_W, RD_H, type DetailOpts } from './runner-detail';
import type { Facing } from './character-motion';

export type NpcStyle = 'ped' | 'cop' | 'heavy' | 'guard' | 'elite' | 'oni' | 'kuma' | 'oyabun' | 'biker' | 'abbot' | 'monk';
type G = CanvasRenderingContext2D;

const PED_TOPS = [mix(K.slate, K.amber, 0.3), mix(K.slate, K.cyan, 0.22), dk(K.white, 0.4), mix(K.night, K.pink, 0.35), dk(K.amber, 0.42), lt(K.slate, 0.12)];
const PED_HAIR = [K.ink, dk(K.amber, 0.62), lt(K.slate, 0.45), mix(K.pink, K.night, 0.45), dk(K.slate, 0.5)];
const ACCENT = [K.amber, K.cyan, K.pink, lt(K.slate, 0.4)];
// classic police blue: slate with a touch of cyan, not the teal it was (owner, 2026-10-14)
const NAVY = mix(dk(K.slate, 0.12), K.pink, 0.06), WINE = mix(K.pink, K.night, 0.55), SUIT = lt(K.night, 0.08), HAORI = mix(K.night, K.slate, 0.15);
// Kaminari-gumi: near-black leather; pink is their trim (owner: the pink jacket matched the Kabuki runner)
const LEATHER = mix(K.night, K.pink, 0.1), RIOT = dk(NAVY, 0.25), ELITE = mix(K.night, K.slate, 0.25);
// Kanemori Sanctuary (M10): Shingan's slate-grey koromo (light enough to read on night ground) under an amber kesa; his security monks in pale grey over plate
const KOROMO = mix(K.slate, K.white, 0.08), KESA = dk(K.amber, 0.38), MONK = dk(lt(K.slate, 0.3), 0.12), MONK_KESA = dk(K.slate, 0.45);
/** Shingan's cyborg hands and skull (a pale metal; his face is behind the mask) */
export const ABBOT_SKIN = lt(K.slate, 0.45);

/** Civilians: outfit 0 suit, 1 hoodie, 2 long coat, 3 jacket and skirt; hair 0 short, 1 long, 2 bun, 3 cap; bag 0 none, 1 crossbody, 2 backpack. */
export const PED_VARIANTS = 12; // bodies are cached per variant, so civilians cycle through 12 looks (x 4 skins)
export function pedVariant(v: number) { return { outfit: v % 4, hair: (v * 3 >> 1) % 4, bag: (v * 5 >> 2) % 3, top: v % 4 === 0 ? dk(K.slate, 0.35) : PED_TOPS[(v * 7) % PED_TOPS.length], hairCol: PED_HAIR[(v * 3) % PED_HAIR.length], accent: ACCENT[v % ACCENT.length] }; }
/** The top (and arm sleeve) colour of each style; the arms in character-art.ts use Look.jacket. */
export function npcTop(s: NpcStyle, v: number): string {
  switch (s) {
    case 'cop': case 'guard': return NAVY;
    case 'heavy': return RIOT;
    case 'elite': return ELITE;
    case 'oni': return WINE;
    case 'kuma': return SUIT;
    case 'oyabun': return HAORI;
    case 'biker': return LEATHER;
    case 'abbot': return KOROMO;
    case 'monk': return MONK;
    default: return pedVariant(v).top;
  }
}
/** Styles whose arms keep the armoured elbow pads and cuffs of the runner rig (Shingan's and the monks' are cyborg joints). */
export const NPC_ARMOUR: ReadonlySet<string> = new Set(['heavy', 'elite', 'biker', 'abbot', 'monk']);

export function npcBodyCanvas(l: Look, view: Facing, o: DetailOpts = {}): HTMLCanvasElement {
  const s = l.npc ?? 'ped', vr = l.v ?? 0, ped = pedVariant(vr);
  const west = view === 'W' || view === 'NW' || view === 'SW';
  const v: Facing = west ? (view === 'W' ? 'E' : view === 'NW' ? 'NE' : 'SE') : view;
  const profile = v === 'E', back = v === 'N' || v === 'NE', diagonal = v === 'SE' || v === 'NE', front = !back && !profile;
  const [c, g] = canvas(RD_W, RD_H), cx = 15, bob = o.bob ?? 0, lean = profile || diagonal ? 1 : 0;
  const top = l.jacket.slice(0, 7), hi = lt(top, 0.28), lo = dk(top, 0.32), deep = dk(top, 0.6);
  const robed = s === 'abbot' || s === 'monk';
  const broad = s === 'heavy' || s === 'elite' || s === 'oyabun' || robed;
  const tw = profile ? (broad ? 10 : 9) : broad ? 16 : diagonal ? 12 : s === 'ped' && ped.outfit === 3 ? 12 : 13, tx = cx - Math.floor(tw / 2) + lean;
  const y = (n: number) => n + bob;

  // ---- legs: trousers, optional knee armour, shoes or tall boots; feet follow travel like the runner rig
  const pants = s === 'cop' || s === 'guard' ? dk(NAVY, 0.25) : s === 'heavy' ? dk(RIOT, 0.2) : s === 'elite' ? dk(ELITE, 0.2)
    : s === 'oni' ? dk(K.slate, 0.45) : s === 'kuma' ? SUIT : s === 'oyabun' ? dk(K.slate, 0.3) : s === 'biker' ? dk(K.slate, 0.62)
    : s === 'abbot' ? dk(KOROMO, 0.3) : s === 'monk' ? dk(K.slate, 0.5)
    : ped.outfit === 0 ? dk(K.slate, 0.4) : ped.outfit === 3 ? dk(K.slate, 0.7) : mix(dk(K.slate, 0.3), K.cyan, 0.12);
  const kneePad = s === 'heavy' || s === 'elite', tall = s === 'heavy' || s === 'elite' || s === 'biker' || s === 'monk';
  const sneaker = s === 'ped' && ped.outfit === 1, wide = s === 'oyabun' || robed;
  const frame = o.frame ?? 0, cycle = frame ? (frame - 1) / 8 * Math.PI * 2 : 0;
  let ta = o.travel ?? Math.PI / 2; if (west) ta = Math.PI - ta;
  const seg = (a: { x: number; y: number }, b: { x: number; y: number }, w: number, col: string) => {
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
    for (let i = 0; i <= n; i++) px(g, Math.round(a.x + (b.x - a.x) * i / n - w / 2), Math.round(a.y + (b.y - a.y) * i / n), w, 1, col);
  };
  const leg = (side: number, far: boolean) => {
    const phase = cycle + (side < 0 ? Math.PI : 0), stride = frame ? Math.sin(phase) : 0, lift = frame ? Math.max(0, Math.cos(phase)) * 2 : 0;
    const col = far ? dk(pants, 0.22) : pants;
    const hip = { x: cx + (profile ? side : side * 2.5), y: 33 };
    const foot = { x: cx + (profile ? side : side * 3) + Math.cos(ta) * stride * 3.5, y: 46 + Math.sin(ta) * stride * 1.5 - lift };
    const knee = { x: hip.x + (foot.x - hip.x) * 0.5 + side * 0.5, y: 39 - lift * 0.4 };
    seg(hip, knee, wide ? 5 : 4, col); if (!far) seg(hip, knee, 1, lt(pants, 0.14));
    seg(knee, { x: foot.x, y: foot.y - 2 }, wide ? 4 : 3, col);
    const kx = Math.round(knee.x - 2), ky = Math.round(knee.y - 1);
    if (tall) { // boot shaft from the knee down
      const bc = s === 'biker' ? K.ink : dk(K.slate, 0.5);
      seg({ x: knee.x, y: knee.y + 2 }, { x: foot.x, y: foot.y - 2 }, 3, far ? dk(bc, 0.2) : bc);
      if (!far) px(g, Math.round(knee.x - 1), Math.round(knee.y + 2), 1, 3, lt(bc, 0.2));
      if (s === 'biker' && !far) px(g, Math.round(knee.x), Math.round(knee.y + 3), 1, 1, K.metalHi); // buckle
    } else if (!far && s === 'ped' && ped.outfit === 1) px(g, kx + 1, ky + 1, 2, 1, lt(pants, 0.25)); // worn denim knee
    if (kneePad) { px(g, kx, ky, 4, 3, far ? K.metalLo : K.metal); px(g, kx + 1, ky, 2, 1, far ? K.metalLo : K.metalHi); }
    const fw = profile ? 6 : 5, fx = Math.round(foot.x - (profile ? 2 : fw / 2)), fy = Math.round(foot.y - (tall ? 3 : 2));
    const shoe = sneaker ? lt(K.slate, 0.3) : s === 'biker' ? K.ink : dk(K.slate, 0.55);
    px(g, fx, fy, fw, tall ? 3 : 2, far ? dk(shoe, 0.2) : shoe);
    if (!far) px(g, profile ? fx + fw - 2 : fx + 1, fy, 2, 1, sneaker ? K.white : s === 'kuma' || s === 'oni' ? K.metalHi : lt(shoe, 0.2)); // toe shine
    px(g, fx, fy + (tall ? 2 : 1), fw, 1, sneaker ? dk(K.white, 0.15) : K.ink); // sole
  };
  leg(-1, true); leg(1, false);

  // ---- skirt (civilian outfit 3) and hips
  if (s === 'ped' && ped.outfit === 3) {
    const sk = dk(ped.accent, 0.45);
    poly(g, [[tx + 1, y(30)], [tx + tw - 1, y(30)], [tx + tw + 1, 37], [tx - 1, 37]], sk);
    px(g, tx, 36, tw, 1, dk(sk, 0.3)); px(g, tx + 2, y(31), 1, 5, lt(sk, 0.2));
  } else px(g, tx + 1, y(31), tw - 2, 3, pants);

  // ---- long coat tails: civilian coat, Benikage's haori (beni lining at the edges)
  const tails = s === 'oyabun' ? HAORI : robed ? top : s === 'ped' && ped.outfit === 2 ? top : null;
  if (tails) {
    // Shingan's koromo falls almost to his feet (only the tabi show); the monks' robes stop above the boot
    const end = s === 'abbot' ? 45 : s === 'oyabun' ? 42 : s === 'monk' ? 41 : 40, flare = s === 'abbot' ? 2 : 1;
    poly(g, [[tx, y(28)], [tx + tw, y(28)], [tx + tw + flare, end], [tx - flare, end]], dk(tails, 0.15));
    px(g, tx - flare, end - 1, tw + flare * 2, 1, dk(tails, 0.45));
    if (s === 'oyabun') { px(g, tx - 1, end - 4, 1, 3, K.pink); px(g, tx + tw, end - 4, 1, 3, dk(K.pink, 0.4)); }
    if (robed) { // pleats, and a lit hem seam on the abbot (the cyborg under the cloth)
      for (let i = 3; i < tw - 1; i += 4) px(g, tx + i, y(32), 1, end - y(32) - 2, dk(tails, 0.3));
      if (s === 'abbot') px(g, tx - flare + 1, end - 2, tw + flare * 2 - 2, 1, dk(K.cyan, 0.55));
    }
    if (front && !back) px(g, cx + lean, y(30), 1, end - y(30), deep); // the opening
  }

  // ---- torso: shoulder line and four tones
  poly(g, [[tx + 2, y(17)], [tx + tw - 2, y(17)], [tx + tw + 1, y(20)], [tx + tw, y(31)], [tx, y(31)], [tx - 1, y(20)]], top);
  px(g, tx, y(20), 1, 10, hi); px(g, tx + 1, y(19), 1, 3, hi); px(g, tx + 2, y(18), tw - 4, 1, hi);
  px(g, tx + tw - 2, y(20), 2, 11, lo); px(g, tx + 1, y(29), tw - 2, 2, deep);
  const belt = (col: string, buckle: string) => { px(g, tx, y(30), tw, 1, col); if (!back) px(g, cx + lean, y(30), 2, 1, buckle); };

  // ---- faction outfits
  const neck = () => px(g, cx - 1 + lean, y(15), 3, 2, dk(l.skin, 0.3));
  const shirtV = (shirt: string, tie?: string) => {
    if (!front && !profile) return;
    const zx = cx + lean + (profile ? 3 : diagonal ? 1 : 0);
    poly(g, [[zx - 2, y(17)], [zx + 3, y(17)], [zx + 1, y(23)]], shirt);
    if (tie) { px(g, zx, y(18), 1, 6, tie); px(g, zx, y(18), 1, 1, lt(tie, 0.3)); }
    if (!profile) { seg({ x: zx - 3, y: y(17) }, { x: zx - 1, y: y(25) }, 1, deep); seg({ x: zx + 4, y: y(17) }, { x: zx + 2, y: y(25) }, 1, deep); } // lapels
  };
  neck();
  if (s === 'cop' || s === 'guard') {
    shirtV(lt(K.cyan, 0.75), K.ink);
    if (front) {
      px(g, tx + 2, y(21), 2, 2, K.amber); px(g, tx + 2, y(21), 1, 1, lt(K.amber, 0.5)); // badge
      px(g, tx + tw - 3, y(18), 2, 3, K.ink); px(g, tx + tw - 3, y(18), 1, 1, K.cyan); // shoulder radio
    }
    if (back) { px(g, tx + 2, y(20), tw - 4, 2, lt(K.slate, 0.25)); px(g, tx + 2, y(22), tw - 4, 1, dk(K.cyan, 0.3)); }
    if (profile) { px(g, tx + tw - 3, y(18), 2, 3, K.ink); px(g, tx + tw - 2, y(18), 1, 1, K.cyan); }
    if (s === 'guard') { // hi-vis vest: amber panels with two reflective bands
      const vest = dk(K.amber, 0.2);
      px(g, tx, y(19), 3, 10, vest); px(g, tx + tw - 3, y(19), 3, 10, dk(vest, 0.2));
      if (back) px(g, tx + 1, y(19), tw - 2, 10, vest);
      for (const r of [23, 27]) px(g, tx, y(r), back ? tw : 3, 1, lt(K.amber, 0.55)), px(g, tx + tw - 3, y(r), 3, 1, lt(K.amber, 0.4));
    }
    belt(K.ink, K.metalHi); px(g, tx + tw - 2, y(30), 2, 3, K.ink); // holster
  } else if (s === 'heavy' || s === 'elite') {
    const plate = s === 'heavy' ? K.metal : dk(K.slate, 0.4), plHi = s === 'heavy' ? K.metalHi : K.metal;
    px(g, tx - 1, y(17), 4, 3, plate); px(g, tx + tw - 3, y(17), 4, 3, dk(plate, 0.2)); px(g, tx - 1, y(17), 3, 1, plHi); // pauldrons
    if (!profile) {
      px(g, tx + 2, y(20), tw - 4, 8, plate); px(g, tx + 2, y(20), tw - 4, 1, plHi); px(g, tx + tw - 3, y(21), 1, 7, dk(plate, 0.3));
      if (s === 'heavy') px(g, tx + 3, y(23), tw - 6, 1, back ? lt(K.slate, 0.3) : dk(K.cyan, 0.15)); // ID band
      else { for (let i = 0; i < 3; i++) { px(g, tx + 3 + i * 3, y(25), 2, 3, K.ink); px(g, tx + 3 + i * 3, y(25), 2, 1, K.metalLo); } px(g, tx + tw - 4, y(20), 1, 1, STATE.danger); }
    } else { px(g, tx + 1, y(20), tw - 2, 8, plate); px(g, tx + 1, y(20), tw - 2, 1, plHi); }
    belt(K.ink, K.metalHi); px(g, tx, y(30), 2, 3, K.metalLo); px(g, tx + tw - 2, y(30), 2, 3, K.metalLo);
  } else if (s === 'oni') {
    if (front || profile) { // open sukajan over a white tee, gold chain
      const ox = cx + lean + (profile ? 3 : diagonal ? 1 : 0);
      px(g, ox - 1, y(17), 3, 12, K.white); px(g, ox + 1, y(18), 1, 11, dk(K.white, 0.2));
      for (let i = 0; i < 3; i++) { px(g, ox - 2 + i, y(18) + i * 2, 1, 1, K.amber); px(g, ox + 2 - i, y(18) + i * 2, 1, 1, dk(K.amber, 0.2)); }
      px(g, ox, y(24), 1, 1, lt(K.amber, 0.5));
      if (!profile) px(g, tx + 1, y(22), 2, 2, K.amber); // embroidered chest crest
    }
    if (back) { // embroidered dragon on the back: an amber zigzag with a pink eye
      const pts = [[0, 0], [1, 1], [2, 0], [3, 1], [4, 2], [3, 3], [2, 4], [3, 5], [4, 6], [5, 6]];
      for (const [dx, dy] of pts) px(g, tx + 3 + dx, y(20) + dy, 2, 1, dx % 2 ? dk(K.amber, 0.2) : K.amber);
      px(g, tx + 4, y(20), 1, 1, K.pink);
    }
    px(g, tx + 1, y(29), tw - 2, 1, mix(K.pink, K.night, 0.2)); px(g, tx + 1, y(30), tw - 2, 1, K.ink); // ribbed hem
  } else if (s === 'kuma' || s === 'oyabun') {
    if (s === 'kuma') { shirtV(K.white, K.ink); if (front) px(g, tx + 2, y(21), 2, 1, K.amber); belt(K.ink, K.metal); }
    else {
      if (front || profile) { // kimono collar: off-white under-collar, a beni lapel line down to the cord
        const zx = cx + lean + (profile ? 3 : diagonal ? 1 : 0);
        seg({ x: zx - 3, y: y(17) }, { x: zx, y: y(24) }, 1, K.white); seg({ x: zx + 3, y: y(17) }, { x: zx + 1, y: y(24) }, 1, dk(K.white, 0.2));
        seg({ x: zx - 2, y: y(17) }, { x: zx, y: y(22) }, 1, K.pink);
        px(g, zx - 2, y(25), 5, 1, K.white); px(g, zx, y(25), 1, 2, K.amber); // haori cord
      }
      if (back) { // family crest: a ring with a lit dot
        const mx = cx + lean - 2, my = y(21);
        px(g, mx + 1, my, 3, 1, K.amber); px(g, mx + 1, my + 4, 3, 1, K.amber); px(g, mx, my + 1, 1, 3, K.amber); px(g, mx + 4, my + 1, 1, 3, K.amber); px(g, mx + 2, my + 2, 1, 1, lt(K.amber, 0.5));
      }
      px(g, tx, y(30), tw, 1, dk(K.pink, 0.35)); // obi
    }
  } else if (robed) {
    const kesa = s === 'abbot' ? KESA : MONK_KESA, kHi = lt(kesa, 0.3);
    if (s === 'monk') { // security plate under the robe: shoulder pauldrons and a lit Kanemori tag
      px(g, tx - 1, y(17), 4, 3, K.metal); px(g, tx + tw - 3, y(17), 4, 3, K.metalLo); px(g, tx - 1, y(17), 3, 1, K.metalHi);
      if (front) { px(g, tx + tw - 5, y(21), 2, 2, K.ink); px(g, tx + tw - 5, y(21), 1, 1, K.cyan); }
    }
    if (front || profile) { // crossed kimono collar in white
      const zx = cx + lean + (profile ? 3 : diagonal ? 1 : 0);
      seg({ x: zx - 3, y: y(17) }, { x: zx, y: y(24) }, 1, K.white); seg({ x: zx + 3, y: y(17) }, { x: zx + 1, y: y(24) }, 1, dk(K.white, 0.25));
    }
    // the kesa: a sash from the left shoulder to the right hip, with a lighter rim
    if (!profile) {
      const [a, b] = back ? [tx + tw - 3, tx + 2] : [tx + 2, tx + tw - 3];
      seg({ x: a, y: y(17) }, { x: b, y: y(29) }, 3, kesa); seg({ x: a, y: y(17) }, { x: b, y: y(29) }, 1, kHi);
    } else { px(g, tx + 1, y(18), 3, 11, kesa); px(g, tx + 1, y(18), 1, 11, kHi); }
    if (s === 'abbot') {
      if (front) for (let i = 0; i < 5; i++) px(g, cx - 4 + lean + i * 2, y(17) + (i === 2 ? 2 : i % 4 ? 1 : 0), 1, 1, i === 2 ? lt(K.amber, 0.4) : K.amber); // juzu beads
      if (back) { px(g, cx + lean, y(17), 1, 7, K.metalLo); for (const r of [18, 20, 22]) px(g, cx + lean, y(r), 1, 1, K.cyan); } // spine ports
      if (profile) { px(g, tx + 1, y(17), 1, 6, K.metalLo); px(g, tx + 1, y(19), 1, 1, K.cyan); }
    }
    px(g, tx, y(30), tw, 1, dk(kesa, 0.4)); // obi
  } else if (s === 'biker') {
    px(g, tx - 1, y(17), tw + 2, 2, K.ink); px(g, tx - 1, y(19), tw + 2, 1, K.pink); // black yoke with a pink edge
    for (let i = 0; i < tw; i += 3) px(g, tx + i, y(17), 1, 1, K.metalHi); // studs
    px(g, tx, y(20), 1, 9, dk(K.pink, 0.2)); px(g, tx + tw - 1, y(20), 1, 9, dk(K.pink, 0.45)); // pink side piping
    if (front) { seg({ x: tx + tw - 3, y: y(20) }, { x: cx + lean - 1, y: y(29) }, 1, K.metalHi); px(g, tx + 2, y(22), 3, 1, K.pink); px(g, tx + 2, y(23), 3, 1, dk(K.pink, 0.4)); }
    if (profile) { px(g, tx + 3, y(20), 1, 9, K.metalHi); px(g, tx + 5, y(22), 2, 4, K.pink); }
    if (back) { // 雷: a pink lightning bolt with a lit core
      const bx = cx + lean;
      for (const [dx, dy, w] of [[1, 0, 2], [0, 1, 2], [-1, 2, 2], [-1, 3, 4], [1, 4, 2], [0, 5, 2], [-1, 6, 2]]) px(g, bx + dx, y(20) + dy, w, 1, K.pink);
      px(g, bx + 1, y(20), 1, 1, lt(K.pink, 0.6)); px(g, bx, y(23), 2, 1, lt(K.pink, 0.4));
    }
    px(g, tx + 1, y(29), tw - 2, 1, K.pink); belt(K.ink, K.metalHi); // pink hem band
  } else { // civilians
    if (ped.outfit === 0) { shirtV(K.white, ped.accent); belt(K.ink, K.metal); }
    else if (ped.outfit === 1) { // hoodie: hood rim at the neck, drawstrings, pocket
      px(g, cx - 4 + lean, y(16), 9, 2, lo);
      if (back) { poly(g, [[cx - 4 + lean, y(16)], [cx + 4 + lean, y(16)], [cx + 3 + lean, y(21)], [cx - 3 + lean, y(21)]], lo); px(g, cx - 3 + lean, y(16), 7, 1, hi); }
      else if (!profile) { px(g, cx - 1 + lean, y(18), 1, 4, K.white); px(g, cx + 2 + lean, y(18), 1, 3, K.white); px(g, tx + 2, y(25), tw - 4, 3, lo); px(g, tx + 2, y(25), tw - 4, 1, hi); }
      else px(g, tx + 1, y(25), 4, 3, lo);
      px(g, tx + 1, y(30), tw - 2, 1, deep);
    } else if (ped.outfit === 2) { // long coat: collar, buttons, belt tie
      if (front) { px(g, cx - 3 + lean, y(17), 2, 3, hi); px(g, cx + 2 + lean, y(17), 2, 3, lo); for (const r of [21, 24, 27]) px(g, cx + 1 + lean, y(r), 1, 1, K.metalHi); }
      px(g, tx, y(28), tw, 1, deep); if (!back) px(g, cx + lean, y(28), 2, 1, ped.accent);
    } else { // cropped jacket over a tee
      if (front) { px(g, cx - 1 + lean, y(18), 3, 9, ped.accent); px(g, cx - 1 + lean, y(18), 3, 1, lt(ped.accent, 0.3)); }
    }
    if (ped.bag === 1) { // crossbody strap and a small bag on the far hip
      if (!back) seg({ x: tx + tw - 2, y: y(18) }, { x: tx + 2, y: y(28) }, 1, dk(ped.accent, 0.5));
      else seg({ x: tx + 2, y: y(18) }, { x: tx + tw - 2, y: y(28) }, 1, dk(ped.accent, 0.5));
      px(g, profile ? tx - 1 : tx, y(27), 4, 3, dk(ped.accent, 0.3)); px(g, profile ? tx - 1 : tx, y(27), 4, 1, ped.accent);
    } else if (ped.bag === 2) {
      if (back) { px(g, tx + 2, y(19), tw - 4, 9, dk(ped.accent, 0.35)); px(g, tx + 2, y(19), tw - 4, 1, ped.accent); px(g, tx + 3, y(23), tw - 6, 2, dk(ped.accent, 0.55)); }
      else if (profile) { px(g, tx - 2, y(19), 3, 9, dk(ped.accent, 0.35)); px(g, tx - 2, y(19), 3, 1, ped.accent); }
      else { px(g, tx + 2, y(18), 1, 6, dk(ped.accent, 0.4)); px(g, tx + tw - 3, y(18), 1, 6, dk(ped.accent, 0.4)); }
    }
  }

  // ---- head: jaw, cheek shadow, ear, face
  const hx = cx - 5 + lean, hy = y(4);
  poly(g, [[hx + 2, hy], [hx + 8, hy], [hx + 10, hy + 2], [hx + 10, hy + 8], [hx + 7, hy + 11], [hx + 3, hy + 11], [hx, hy + 8], [hx, hy + 2]], l.skin);
  px(g, hx + 8, hy + 3, 2, 6, dk(l.skin, 0.22)); px(g, hx + 2, hy + 9, 6, 1, dk(l.skin, 0.15));
  const eyes = (col = K.ink) => {
    if (back) return;
    if (profile) { px(g, hx + 7, hy + 5, 1, 1, col); px(g, hx + 10, hy + 6, 1, 1, l.skin); px(g, hx + 3, hy + 5, 2, 2, dk(l.skin, 0.3)); return; } // eye, nose, ear
    const e0 = diagonal ? hx + 4 : hx + 3; px(g, e0, hy + 5, 1, 1, col); px(g, e0 + 3, hy + 5, 1, 1, col);
    px(g, e0 + 1, hy + 8, 2, 1, dk(l.skin, 0.4)); // mouth
  };
  const shades = () => {
    if (back) return;
    if (profile) { px(g, hx + 5, hy + 4, 5, 2, K.ink); px(g, hx + 8, hy + 4, 1, 1, K.cyan); px(g, hx + 3, hy + 4, 2, 1, K.ink); return; }
    const e0 = diagonal ? hx + 3 : hx + 2; px(g, e0, hy + 4, 7, 2, K.ink); px(g, e0 + 1, hy + 4, 1, 1, K.cyan); px(g, e0 + 5, hy + 4, 1, 1, dk(K.cyan, 0.3));
  };
  const hair = (col: string, style: 'short' | 'long' | 'bun' | 'slick' | 'pomp' | 'knot') => {
    const hHi = lt(col, 0.28), hLo = dk(col, 0.3);
    poly(g, [[hx, hy + 3], [hx, hy + 1], [hx + 2, hy - 1], [hx + 8, hy - 1], [hx + 10, hy + 1], [hx + 10, hy + 3], [hx + 7, hy + 2], [hx + 3, hy + 2]], col);
    px(g, hx + 3, hy - 1, 4, 1, hHi); px(g, hx + 8, hy + 1, 2, 1, hLo);
    if (back) { px(g, hx + 1, hy + 2, 8, style === 'long' ? 9 : 6, col); px(g, hx + 2, hy + 2, 2, 3, hHi); px(g, hx + 2, hy + (style === 'long' ? 10 : 7), 6, 1, hLo); }
    else if (profile) { px(g, hx, hy + 2, 4, style === 'long' ? 9 : 5, col); px(g, hx + 1, hy + 2, 1, 3, hHi); }
    else { px(g, hx, hy + 2, 1, style === 'long' ? 9 : 4, col); px(g, hx + 9, hy + 2, 1, style === 'long' ? 9 : 3, hLo); }
    if (style === 'long' && !back) px(g, profile ? hx : hx - 1, hy + 6, 1, 6, hLo);
    if (style === 'bun' || style === 'knot') { const bx = profile ? hx + 1 : hx + 3; px(g, bx, hy - 3, 4, 3, col); px(g, bx + 1, hy - 3, 2, 1, hHi); if (style === 'knot') px(g, bx, hy - 1, 4, 1, K.amber); }
    if (style === 'pomp') { const qx = profile ? hx + 4 : hx + 2; px(g, qx, hy - 3, 7, 3, col); px(g, qx + 1, hy - 3, 5, 1, hHi); if (profile) px(g, qx + 6, hy - 2, 2, 2, col); }
    if (style === 'slick') px(g, hx + 2, hy, 6, 1, hHi);
  };
  const kumadori = () => { // red kumadori sweeps over the eyes and cheeks
    if (back) return; const r = STATE.danger;
    if (profile) { px(g, hx + 6, hy + 3, 1, 1, r); px(g, hx + 7, hy + 7, 2, 1, r); return; }
    const e0 = diagonal ? hx + 4 : hx + 3; px(g, e0 - 1, hy + 4, 1, 1, r); px(g, e0 + 4, hy + 4, 1, 1, r); px(g, e0 - 1, hy + 7, 2, 1, r); px(g, e0 + 3, hy + 7, 2, 1, r);
  };
  const helmet = (shell: string, shellHi: string, visor: string | null, slit = false) => {
    poly(g, [[hx - 1, hy + 4], [hx - 1, hy + 1], [hx + 2, hy - 2], [hx + 8, hy - 2], [hx + 11, hy + 1], [hx + 11, hy + 8], [hx - 1, hy + 8]], shell);
    px(g, hx + 2, hy - 1, 5, 1, shellHi); px(g, hx, hy + 1, 1, 3, shellHi); px(g, hx + 9, hy + 1, 2, 7, dk(shell, 0.3));
    if (back) { px(g, hx - 1, hy + 8, 12, 2, dk(shell, 0.35)); return; }
    if (!visor) return;
    const vx = profile ? hx + 5 : diagonal ? hx + 1 : hx, vw = profile ? 6 : 10;
    if (slit) { px(g, vx, hy + 4, vw, 5, shell); px(g, vx, hy + 5, vw, 1, visor); px(g, vx + vw - 2, hy + 5, 1, 1, lt(visor, 0.5)); px(g, vx + 1, hy + 9, vw - 2, 2, dk(shell, 0.2)); }
    else { px(g, vx, hy + 4, vw, 3, dk(visor, 0.45)); px(g, vx, hy + 4, vw, 1, visor); px(g, vx + vw - 3, hy + 5, 1, 1, K.white); }
  };

  switch (s) {
    case 'cop': case 'guard': {
      hair(K.ink, 'short'); eyes();
      if (s === 'cop') { // peaked cap: navy crown, black band, peak, amber badge; cyan HUD eyepiece
        poly(g, [[hx - 1, hy + 2], [hx, hy - 2], [hx + 10, hy - 2], [hx + 11, hy + 2]], NAVY);
        px(g, hx + 1, hy - 2, 7, 1, lt(NAVY, 0.25)); px(g, hx - 1, hy + 2, 12, 1, K.ink);
        if (!back) { px(g, profile ? hx + 6 : hx, hy + 3, profile ? 6 : 10, 1, K.ink); px(g, profile ? hx + 7 : hx + 4, hy, 2, 2, K.amber); px(g, profile ? hx + 7 : diagonal ? hx + 7 : hx + 6, hy + 5, 2, 1, K.cyan); }
      } else { // checkpoint helmet: pale shell, cyan stripe, chin strap
        helmet(lt(K.slate, 0.45), lt(K.slate, 0.7), null);
        px(g, hx + 4, hy - 2, 2, 6, dk(K.cyan, 0.2));
        if (!back) px(g, profile ? hx + 4 : hx, hy + 8, 1, 3, K.ink);
      }
      break;
    }
    case 'heavy': helmet(K.metalLo, K.metal, K.cyan); break;
    case 'elite': helmet(dk(K.slate, 0.55), dk(K.slate, 0.3), STATE.danger, true); break;
    case 'biker': helmet(K.night, mix(K.night, K.slate, 0.5), K.pink); px(g, hx + 4, hy - 2, 2, back ? 10 : 6, K.pink); px(g, hx + 4, hy - 2, 1, 2, lt(K.pink, 0.5)); break;
    case 'oni': hair(K.ink, 'pomp'); eyes(); shades(); break;
    case 'kuma': hair(K.ink, 'slick'); eyes(); kumadori(); shades(); if (!back) px(g, profile ? hx + 3 : hx, hy + 5, 1, 4, K.amber); break; // earpiece wire
    case 'oyabun': hair(K.white, 'knot'); eyes(); kumadori(); break;
    case 'abbot': { // shaved metal skull with a lit seam, a white featureless mask: no eyes, no mouth
      px(g, hx + 2, hy, 6, 1, lt(l.skin, 0.25));
      if (back) { px(g, hx + 5, hy, 1, 9, dk(K.cyan, 0.2)); px(g, hx + 2, hy + 7, 7, 1, K.white); break; } // the mask's cord
      if (profile) {
        poly(g, [[hx + 6, hy + 1], [hx + 10, hy + 2], [hx + 11, hy + 6], [hx + 9, hy + 11], [hx + 6, hy + 11]], K.white);
        px(g, hx + 6, hy + 2, 1, 9, dk(K.white, 0.2)); px(g, hx + 3, hy + 1, 1, 6, dk(K.cyan, 0.2)); break;
      }
      const mx = diagonal ? hx + 1 : hx;
      poly(g, [[mx + 2, hy], [mx + 8, hy], [mx + 10, hy + 2], [mx + 10, hy + 8], [mx + 7, hy + 11], [mx + 3, hy + 11], [mx, hy + 8], [mx, hy + 2]], K.white);
      px(g, mx + 8, hy + 2, 2, 7, dk(K.white, 0.18)); px(g, mx + 3, hy + 10, 4, 1, dk(K.white, 0.25)); px(g, mx + 3, hy + 1, 3, 1, lt(K.white, 0.5));
      break;
    }
    case 'monk': { // shaved head, cyan visor band, black respirator over the mouth
      px(g, hx + 2, hy, 6, 1, lt(l.skin, 0.2));
      if (back) { px(g, hx - 1, hy + 4, 12, 1, K.ink); break; }
      const vx = profile ? hx + 5 : diagonal ? hx + 1 : hx, vw = profile ? 6 : 10;
      px(g, vx, hy + 4, vw, 2, K.ink); px(g, vx, hy + 4, vw, 1, K.cyan); px(g, vx + vw - 2, hy + 4, 1, 1, K.white);
      px(g, vx + (profile ? 1 : 2), hy + 7, profile ? 5 : 6, 3, K.ink); px(g, vx + (profile ? 2 : 3), hy + 8, profile ? 3 : 4, 1, K.metalLo);
      break;
    }
    default: {
      hair(ped.hairCol, (['short', 'long', 'bun', 'short'] as const)[ped.hair]); eyes();
      if (ped.hair === 3) { // a soft cap in the outfit's accent
        poly(g, [[hx - 1, hy + 2], [hx + 1, hy - 2], [hx + 9, hy - 2], [hx + 11, hy + 2]], dk(ped.accent, 0.35));
        px(g, hx + 2, hy - 2, 5, 1, dk(ped.accent, 0.1));
        if (!back) px(g, profile ? hx + 7 : hx + 1, hy + 2, profile ? 5 : 8, 1, dk(ped.accent, 0.55));
      }
    }
  }

  let out = outline(c);
  if (west) { const [m, mg] = canvas(RD_W, RD_H); mg.translate(RD_W, 0); mg.scale(-1, 1); mg.drawImage(out, 0, 0); out = m; }
  if (o.extra) o.extra(out.getContext('2d')!);
  return out;
}
